// O catálogo de insumos da área de trabalho (planning-workspace): uma LISTA TABULAR — insumo,
// família/classificação ERP, previsto, regra vigente e ação — com busca e filtro de
// classificação como controles distintos e rotulados, indicadores que também são o filtro do
// que contam, paginação da plataforma (com "Página X de Y") e estado vazio com "limpar filtros".
//
// A busca e a classificação são resolvidas no SERVIDOR, antes da paginação: a lista mostra o
// catálogo inteiro da obra, não só a página carregada. Os contadores vêm do `summary` do
// servidor e não mudam com o filtro — o que muda é a lista.
import { useEffect, useState } from "react";

import Pagination from "../offcanvas/pagination";
import { fetchDisbursementTargets } from "./disbursementApi";
import { money } from "./format";

const PAGE_SIZE = 50;

const reasonText = (reason) => {
  if (reason === "no_service_composition")
    return "Sem composição vinculada a um serviço planejado.";
  if (reason === "composition_missing")
    return "Composição do insumo indisponível.";
  if (reason === "planned_series_missing")
    return "Previsto do serviço indisponível.";
  return "Este insumo ainda não tem dados suficientes para definir uma regra.";
};
const inheritedLabel = (source) =>
  typeof source === "string" ? source : source?.label ?? source?.key;
const actionLabel = (item) => {
  if (item.ruleId) return "Editar regra";
  return item.inheritedFrom ? "Sobrescrever aqui" : "Definir";
};

// O vocabulário do filtro de classificação é o do servidor; cada indicador aplica o seu.
const FILTERS = Object.freeze([
  { value: "all", label: "Todos os insumos", indicator: "Total" },
  { value: "eligible", label: "Com previsto disponível", indicator: "Com previsto disponível" },
  { value: "ineligible", label: "Sem dados suficientes", indicator: "Sem dados suficientes" },
  { value: "unclassified", label: "Sem classificação ERP", indicator: "Sem classificação ERP" },
]);

/** Folha da linha: `money()` lê o contexto do projeto (um hook disfarçado). */
const ResourceRow = ({ item, readOnly, followsLabel, rulesByRuleId, onDefineRule }) => {
  const planned = money(item.isEligible === true ? item.plannedCents : null);
  let rule = followsLabel ?? "Segue a origem";
  if (item.ruleId) rule = "Regra própria";
  else if (item.inheritedFrom) rule = `Regra herdada: ${inheritedLabel(item.inheritedFrom)}`;
  return (
    <tr>
      <td>
        <strong>{item.label}</strong>
        {item.isEligible !== true ? <p>{reasonText(item.reason)}</p> : null}
      </td>
      <td>{item.isClassified ? item.erpGroup : "Sem classificação ERP"}</td>
      <td className="ampli-catalog__number">{planned}</td>
      <td>{rule}</td>
      <td>
        {!readOnly && item.isEligible === true ? (
          <button
            type="button"
            className="button button-outline small"
            disabled={Boolean(item.ruleId && !rulesByRuleId[item.ruleId])}
            onClick={() => onDefineRule?.(item)}
          >
            {actionLabel(item)}
          </button>
        ) : null}
      </td>
    </tr>
  );
};

const ResourceCatalog = ({
  workId,
  readOnly = false,
  onDefineRule,
  initialClassification = "all",
  reloadKey,
  rulesByRuleId = {},
  followsLabel = null,
}) => {
  const [query, setQuery] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    search: "",
    classification: initialClassification,
  });
  const [search, setSearch] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setResult(null);
    setError(false);
    fetchDisbursementTargets(workId, { level: "resource", ...query })
      .then((payload) => {
        if (!active) return;
        if (
          !payload ||
          !Array.isArray(payload.items) ||
          !Number.isSafeInteger(payload.total) ||
          payload.total < 0 ||
          payload.page !== query.page ||
          payload.pageSize !== query.pageSize ||
          typeof payload.hasMore !== "boolean"
        ) {
          setError(true);
          return;
        }
        setResult(payload);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [workId, query, reloadKey]);

  const applyClassification = (classification) =>
    setQuery((current) => ({ ...current, classification, page: 0 }));

  const clearFilters = () => {
    setSearch("");
    setQuery((current) => ({ ...current, search: "", classification: "all", page: 0 }));
  };

  const summary = result?.summary ?? null;
  const countOf = (value) => {
    if (!summary) return null;
    if (value === "all") return summary.eligible + summary.ineligible;
    return summary[value];
  };
  const filtered = query.search !== "" || query.classification !== "all";
  const pageCount = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;

  return (
    <section aria-label="Catálogo de insumos" className="ampli-catalog">
      <h3>Catálogo de insumos</h3>
      <p>
        Insumos com e sem regra própria. A busca e a classificação abrangem o
        catálogo inteiro.
      </p>

      <div className="ampli-catalog__indicators" role="group" aria-label="Contadores do catálogo">
        {FILTERS.map((filter) => {
          const count = countOf(filter.value);
          const active = query.classification === filter.value;
          return (
            <button
              key={filter.value}
              type="button"
              className={`ampli-catalog__indicator${active ? " active" : ""}`}
              aria-pressed={active}
              disabled={loading && !summary}
              onClick={() => applyClassification(filter.value)}
            >
              <span className="card-label">{filter.indicator}</span>
              <strong>{count === null ? "—" : count.toLocaleString("pt-BR")}</strong>
            </button>
          );
        })}
      </div>

      <form
        className="ampli-catalog__filters"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery((current) => ({
            ...current,
            page: 0,
            search: search.trim(),
          }));
        }}
      >
        <label>
          <span>Buscar insumos</span>
          <input
            className="resource-search"
            aria-label="Buscar insumos"
            maxLength={80}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <button type="submit" className="button button-outline small" disabled={loading}>
          Buscar
        </button>
        <label>
          <span>Classificação</span>
          <select
            className="default-input"
            aria-label="Classificação dos insumos"
            value={query.classification}
            onChange={(event) => applyClassification(event.target.value)}
          >
            {FILTERS.map((filter) => (
              <option key={filter.value} value={filter.value}>
                {filter.label}
              </option>
            ))}
          </select>
        </label>
      </form>

      {loading ? <p role="status">Carregando insumos…</p> : null}
      {error ? (
        <p role="alert">
          Não foi possível carregar o catálogo completo. Tente buscar novamente.
        </p>
      ) : null}
      {result ? (
        <>
          <p data-testid="catalog-found">{`${result.total.toLocaleString("pt-BR")} ${
            result.total === 1 ? "insumo encontrado" : "insumos encontrados"
          }`}</p>
          {result.reason ? (
            <p role="status">
              A fonte está parcial ou indisponível; confira os avisos de cada
              insumo.
            </p>
          ) : null}
          {!result.items.length ? (
            <div className="empty-state">
              <p>
                {result.reason
                  ? "Catálogo indisponível para esta fonte."
                  : "Nenhum insumo encontrado."}
              </p>
              {filtered ? (
                <button
                  type="button"
                  className="button button-outline small"
                  onClick={clearFilters}
                >
                  Limpar filtros
                </button>
              ) : null}
            </div>
          ) : (
            <table className="ampli-catalog__table">
              <thead>
                <tr>
                  <th scope="col">Insumo</th>
                  <th scope="col">Família / classificação ERP</th>
                  <th scope="col">Previsto</th>
                  <th scope="col">Regra vigente</th>
                  <th scope="col">Ação</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((item) => (
                  <ResourceRow
                    key={item.key}
                    item={item}
                    readOnly={readOnly}
                    followsLabel={followsLabel}
                    rulesByRuleId={rulesByRuleId}
                    onDefineRule={onDefineRule}
                  />
                ))}
              </tbody>
            </table>
          )}
          {result.total > 0 ? (
          <Pagination
            count={result.total}
            perPage={result.pageSize}
            currentPage={result.page + 1}
            pageCount={pageCount}
            disableBack={loading || result.page === 0}
            disableForward={loading || !result.hasMore}
            goBack={() =>
              setQuery((current) => ({ ...current, page: current.page - 1 }))
            }
            goForward={() =>
              setQuery((current) => ({ ...current, page: current.page + 1 }))
            }
          />
          ) : null}
        </>
      ) : null}
    </section>
  );
};
export default ResourceCatalog;
